/**
 * Main product. Drives `sections/main-product.liquid`:
 *
 *   gallery    keeps the mobile rail and the desktop thumbnails in step with
 *              the track, and moves the track when a thumbnail is clicked. The
 *              swipe itself is native scroll snap and needs none of this
 *   subscribe  One-time / Subscribe & Save. Opens the frequency panel, enables
 *              the `selling_plan` select only while Subscribe is chosen (a
 *              disabled field is not posted), and reprices the tiles and the
 *              add to bag from the product JSON
 *   add        through `DDJCart.add` (one request that opens and fills the bag),
 *              or, without the bag, AJAX add to /cart/add.js, then the same `cart:change` event and
 *              `[data-cart-count]` update that quick add uses, so the bag
 *              drawer picks both up when it lands
 *
 * Every price arrives pre-formatted by Shopify in the section's product JSON,
 * so nothing here formats money.
 *
 * Section-scoped and idempotent, and re-run on `shopify:section:load`, so the
 * theme editor re-render needs no reload.
 */
(function () {
  var RESET_AFTER = 2000;

  function root() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  /* ─── Gallery ─── */

  function initGallery(gallery) {
    var track = gallery.querySelector('[data-gallery-track]');
    if (!track) return;

    var slides = track.querySelectorAll('[data-gallery-slide]');
    var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    var rail = gallery.querySelector('[data-gallery-rail]');
    var current = -1;

    function index() {
      var width = track.clientWidth;
      if (!width) return 0;
      return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / width)));
    }

    function update() {
      var i = index();
      if (i === current) return;
      current = i;

      if (rail) rail.style.transform = 'translateX(' + i * 100 + '%)';

      Array.prototype.forEach.call(thumbs, function (thumb) {
        if (Number(thumb.dataset.galleryThumb) === i) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    var pending = false;
    track.addEventListener('scroll', function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        update();
      });
    }, { passive: true });

    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.addEventListener('click', function () {
        var slide = slides[Number(thumb.dataset.galleryThumb)];
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      });
    });

    update();
  }

  /* ─── Purchase options and pricing ─── */

  function initBuy(section) {
    var form = section.querySelector('[data-product-form]');
    var jsonNode = section.querySelector('[data-product-json]');
    if (!form || !jsonNode) return;

    var data;
    try {
      data = JSON.parse(jsonNode.textContent);
    } catch (error) {
      return;
    }

    var variantInput = form.querySelector('[data-variant-id]');
    var planSelect = section.querySelector('[data-selling-plan]');
    var panel = section.querySelector('[data-subscribe-panel]');
    var options = section.querySelectorAll('[data-purchase-option]');
    var button = form.querySelector('[data-add-to-cart]');
    var errorNode = form.querySelector('[data-cart-error]');

    function setText(selector, value) {
      var nodes = section.querySelectorAll(selector);
      Array.prototype.forEach.call(nodes, function (node) {
        node.textContent = value || '';
        node.hidden = !value;
      });
    }

    function currentVariant() {
      var id = variantInput ? Number(variantInput.value) : NaN;
      for (var i = 0; i < data.variants.length; i++) {
        if (data.variants[i].id === id) return data.variants[i];
      }
      return data.variants[0];
    }

    function subscribing() {
      var chosen = section.querySelector('[data-purchase-option]:checked');
      return chosen ? chosen.value === 'subscribe' : false;
    }

    function render() {
      var variant = currentVariant();
      var plan = planSelect ? variant.plans[planSelect.value] : null;
      var subscribe = subscribing() && plan;

      if (panel) panel.hidden = !subscribing();
      if (planSelect) planSelect.disabled = !subscribing();

      section.querySelectorAll('[data-plan-details]').forEach(function (node) {
        node.hidden = !planSelect || node.dataset.planDetails !== planSelect.value;
      });

      setText('[data-header-price]', variant.price);
      setText('[data-header-compare]', variant.compare);
      setText('[data-one-time-price]', variant.price);

      if (plan) {
        setText('[data-subscribe-price]', plan.price);
        setText('[data-subscribe-saving]', plan.saving && plan.saving !== '0' ? ' ' + plan.saving + '%' : '');
      }

      /* Subscribing shows what it saves: the plan's price, with the one-time
       * price it replaces struck through beside it. */
      setText('[data-atc-price]', subscribe ? plan.price : variant.price);
      setText('[data-atc-compare]', subscribe ? plan.compare : variant.compare);

      if (button) {
        button.disabled = !variant.available;
        var label = button.querySelector('[data-atc-label]');
        if (label && button.dataset.state !== 'added' && button.dataset.state !== 'adding') {
          label.textContent = variant.available ? data.addLabel : data.soldOutLabel;
        }
      }
    }

    Array.prototype.forEach.call(options, function (option) {
      option.addEventListener('change', render);
    });
    if (planSelect) planSelect.addEventListener('change', render);
    if (variantInput && variantInput.tagName === 'SELECT') variantInput.addEventListener('change', render);

    render();

    /* ─── Add to bag ─── */

    function updateCartCount(cart) {
      document.querySelectorAll('[data-cart-count]').forEach(function (node) {
        node.textContent = node.textContent.replace(/\((\d+)\)/, '(' + cart.item_count + ')');
      });
    }

    function showError(message) {
      if (!errorNode) return;
      errorNode.textContent = message || '';
      errorNode.hidden = !message;
    }

    form.addEventListener('submit', function (event) {
      if (!window.fetch || !window.FormData) return;
      event.preventDefault();
      if (!button || button.disabled) return;

      showError('');
      button.disabled = true;
      /* A second add while the first still says Added: its reset must not
       * land in the middle of this one. */
      window.clearTimeout(button._ddjReset);
      button.dataset.state = 'adding';
      var addingLabel = button.querySelector('[data-atc-label]');
      if (addingLabel && data.addingLabel) addingLabel.textContent = data.addingLabel;

      /* Recharge's legacy ScriptTag (`widget.min.js`) loads on every theme of
       * this store and injects its own radio widget beside any product form,
       * with its own `selling_plan` field. It is hidden in CSS, and here the
       * plan is rebuilt from OUR controls alone, so nothing it adds can turn a
       * one-time add into a subscription or the reverse. */
      var body = new FormData(form);
      body.delete('selling_plan');
      body.delete('purchase_option');
      if (subscribing() && planSelect && planSelect.value) {
        body.append('selling_plan', planSelect.value);
      }

      /* With the bag on the page, one request that opens and fills it
       * (assets/cart.js); the bag shows any refusal itself. */
      var viaBag = !!(window.DDJCart && window.DDJCart.add);
      var adding = viaBag
        ? window.DDJCart.add(body)
        : fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: body
        })
          .then(function (response) {
            return response.json().then(function (body) {
              if (!response.ok) throw body;
              return body;
            });
          })
          .then(function () {
            return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } });
          })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            updateCartCount(cart);
            document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart, source: 'main-product' } }));
          });

      adding
        .then(function () {
          var label = button.querySelector('[data-atc-label]');
          button.dataset.state = 'added';
          if (label) label.textContent = data.addedLabel;

          button._ddjReset = window.setTimeout(function () {
            button.dataset.state = 'idle';
            render();
          }, RESET_AFTER);
        })
        .catch(function (error) {
          button.dataset.state = 'idle';
          var text = (error && error.description) || data.errorLabel;
          showError(text);
          /* The bag drawer shows the same message (assets/cart.js). */
          if (!viaBag) document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: text, source: 'main-product' } }));
        })
        .then(function () {
          if (button.dataset.state !== 'added') render();
          else button.disabled = false;
        });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-main-product]').forEach(function (section) {
      if (section.dataset.mainProductReady === 'true') return;
      section.dataset.mainProductReady = 'true';

      section.querySelectorAll('[data-product-gallery]').forEach(initGallery);
      initBuy(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
